import type { LucideIcon } from 'lucide-react';
import { Tab, TabList, Tabs as AriaTabs } from 'react-aria-components';
import { cn } from '../../utils/cn.js';

export interface TabItem {
  id: string;
  label: string;
  /** Small count next to the label (e.g. items in that tab). */
  count?: number;
  icon?: LucideIcon;
  isDisabled?: boolean;
}

interface TabsProps {
  tabs: readonly TabItem[];
  activeTab: string;
  onChange: (id: string) => void;
  /** `underline` (default) for page sections; `segmented` for a compact switch inside a card. */
  variant?: 'underline' | 'segmented';
  /** Names the tab strip for screen readers when no heading labels it. */
  'aria-label'?: string;
  className?: string;
}

const LIST = {
  underline: 'flex gap-1 overflow-x-auto overflow-y-hidden border-b border-card-border',
  segmented: 'inline-flex gap-1 overflow-x-auto',
};

const TAB = {
  underline:
    '-mb-px border-b-2 border-transparent py-3 text-text-100 hover:text-title-50 data-selected:border-primary-500 data-selected:text-neutral-brand-color',
  segmented:
    'rounded-lg py-1.5 text-text-100 hover:text-title-50 data-selected:bg-tab-secondary-active-background data-selected:text-neutral-brand-color',
};

/**
 * Tab strip (NextAdmin tab styles on react-aria `Tabs`: arrow keys move between tabs, Home/End jump).
 * Controlled: the page owns the active tab — use `useTabParam` to keep it in `?tab=` so it survives reload and
 * can be linked. Render the active panel yourself below the strip.
 */
export function Tabs({ tabs, activeTab, onChange, variant = 'underline', className, ...aria }: TabsProps) {
  return (
    <AriaTabs
      selectedKey={activeTab}
      onSelectionChange={(key) => onChange(String(key))}
      disabledKeys={tabs.filter((tab) => tab.isDisabled).map((tab) => tab.id)}
      className={className}
    >
      <TabList aria-label={aria['aria-label']} className={LIST[variant]}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <Tab
              key={tab.id}
              id={tab.id}
              className={cn(
                'flex cursor-pointer items-center gap-2 px-3 text-sm font-medium whitespace-nowrap outline-none transition-colors',
                'data-focus-visible:ring-2 data-focus-visible:ring-primary-500 data-disabled:cursor-not-allowed data-disabled:opacity-50',
                TAB[variant],
              )}
            >
              {Icon && <Icon size={15} className="shrink-0" aria-hidden="true" />}
              {tab.label}
              {tab.count !== undefined && (
                <span className="rounded-full bg-background-gray-secondary px-1.5 py-px font-mono text-[11px] leading-4 text-text-100">
                  {tab.count}
                </span>
              )}
            </Tab>
          );
        })}
      </TabList>
    </AriaTabs>
  );
}
